// @ts-check
import { Content, Flex, Heading, IllustratedMessage, Text, Well } from '@adobe/react-spectrum';
import ErrorIcon from '@spectrum-icons/illustrations/Error';
import qs from 'query-string';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { GeolocationContext, NotFoundMessage } from '@exchange-frontends/components';
import { useAnalyticsPageLoad } from '@exchange-frontends/custom-hooks';
import { publisherPage } from '@exchange-frontends/utils';

import useCcPublisherInfo from '../../../../actions/ccextApi/selectors/useCcPublisherInfo';
import useEcPublisherInfo from '../../../../actions/myxchngApi/selectors/useEcPublisherInfo';
import { ContainerContext, informNewRelic } from '../../../../utils';
import { getPartnerBadgeImage } from '../../utils/PartnerBadgeImage';
import ProfileInfo from './ProfileInfo';
import { ProfileLayout } from './ProfileLayout';
import PublisherApps from './PublisherApps';

/**
 * @param {object} props
 * @param {'cc'|'ec'|'dc'} props.type
 */
const Profile = (props) => {
  // Trashalytics at the top to get them out of the way
  useAnalyticsPageLoad(publisherPage);

  const { type } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const usePublisherInfo = type === 'cc' ? useCcPublisherInfo : useEcPublisherInfo;
  const { geolocation } = useContext(GeolocationContext);
  const { ims } = useContext(ContainerContext);
  const { data, isLoading, error } = usePublisherInfo();

  const isEuUser = geolocation === 'EU';
  const showHiddenListingsDisclaimer = type !== 'cc' && isEuUser && !isLoading && !data?.contactDetails;

  const [publisherLogo, setPublisherLogo] = useState(null);

  useEffect(() => {
    const page = qs.parse(location.search).page;
    if (!page) {
      navigate(
        {
          search: qs.stringify(Object.assign({}, qs.parse(location.search), { page: 1 })),
        },
        { replace: true }
      );
    }
  });

  if (isLoading) {
    return (
      <ProfileLayout>
        <ProfileInfo>
          <ProfileInfo.Header loading />
          <ProfileInfo.Body loading />
          <ProfileInfo.Footer loading />
        </ProfileInfo>
      </ProfileLayout>
    );
  }

  if (error) {
    const status = error.fetchResponse?.status;

    if (status === 404) {
      return (
        <ProfileLayout data={{ name: 'Publisher not found' }}>
          <NotFoundMessage
            heading="Page Not Found"
            content="We couldn't locate the page you were looking for. It might have moved or no longer exists. Use the navigation above, or click the link bellow to return to the homepage."
            link={{
              text: 'Return to home',
              href: '/',
            }}
            rootProps={{ flex: 1, marginTop: '-100px' }}
            linkProps={{
              marginTop: 'size-100',
            }}
          />
        </ProfileLayout>
      );
    }

    // CCES asked us to redirect to login on 429
    if (status === 429) {
      ims?.signIn();
      return null;
    }

    const errorId = informNewRelic(error);

    return (
      <ProfileLayout data={{ name: 'Something went wrong' }}>
        <IllustratedMessage flex={1} marginTop="-100px">
          <ErrorIcon />
          <Heading>Something went wrong</Heading>
          <Content>
            <Text>
              We encountered an error while trying to load this publisher&apos;s profile. Please try again later
              {errorId && ' or contact support with the error id below if the problem persists'}.
            </Text>
            {errorId && (
              <Well
                justifySelf="center"
                marginTop="size-100"
                maxWidth="50px"
                UNSAFE_style={{ textAlign: 'center', minInlineSize: '100px' }}
              >
                {errorId}
              </Well>
            )}
          </Content>
        </IllustratedMessage>
      </ProfileLayout>
    );
  }

  if (data) {
    const partnerLevel = 'partnerLevel' in data ? data.partnerLevel : null;

    return (
      <ProfileLayout data={data} publisherLogo={publisherLogo}>
        <ProfileInfo>
          <ProfileInfo.Header
            icon={data.logo ? data.logo : publisherLogo}
            name={data.name}
            level={getPartnerBadgeImage(partnerLevel)}
            loading={isLoading}
          />
          <ProfileInfo.Body loading={isLoading}>
            {data.description ? (
              <Flex gap="size-400" wrap>
                <Text flexGrow={1}>{data.description}</Text>
              </Flex>
            ) : null}
          </ProfileInfo.Body>
          <ProfileInfo.Footer
            loading={isLoading}
            website={data.website}
            contactDetails={data.contactDetails}
            showTraderInfo={isEuUser}
            showHiddenListingsDisclaimer={showHiddenListingsDisclaimer}
          />
        </ProfileInfo>
        <PublisherApps
          setPublisherLogo={setPublisherLogo}
          type={type}
          showHiddenListingsDisclaimer={showHiddenListingsDisclaimer}
        />
      </ProfileLayout>
    );
  }

  return null;
};

export default Profile;
